import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Scale, BookOpen, Menu, X, Globe, Gavel, Shield, MessageSquare, Video, Download 
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { i18n } = useTranslation();

  const toggleLanguage = () => {
    const nextLang = i18n.language === 'ur' ? 'en' : 'ur';
    i18n.changeLanguage(nextLang);
  };

  const navItemStyle = (isActive: boolean, bg: string, textColor: string = 'white') => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.42rem 0.85rem',
    borderRadius: '20px',
    fontSize: '0.82rem',
    fontWeight: '800',
    textDecoration: 'none',
    color: isActive ? '#0f172a' : textColor,
    backgroundColor: isActive ? '#ecc94b' : bg,
    border: isActive ? '1.5px solid #ecc94b' : '1px solid rgba(255,255,255,0.15)',
    boxShadow: isActive ? '0 0 12px rgba(236,201,75,0.6)' : 'none',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap' as const,
    cursor: 'pointer'
  });

  return (
    <header style={{
      width: '100%',
      backgroundColor: '#090d16',
      borderBottom: '2px solid #ecc94b',
      position: 'sticky',
      top: 0,
      zIndex: 9999,
      boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
      boxSizing: 'border-box'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0.65rem 1.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '0.75rem',
        boxSizing: 'border-box'
      }}>
        
        {/* BRAND LOGO & TITLE */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', textDecoration: 'none', flexShrink: 0 }}>
          <img 
            src="/logo_ELum_Firm4.png" 
            alt="Elum Law Firm Logo" 
            style={{ 
              height: '48px', 
              width: '48px', 
              borderRadius: '10px', 
              border: '2px solid #ecc94b',
              boxShadow: '0 0 12px rgba(236,201,75,0.4)',
              objectFit: 'cover'
            }} 
          />
          <div>
            <span style={{ 
              display: 'block', 
              fontSize: '1.2rem', 
              fontWeight: '900', 
              color: '#ecc94b', 
              letterSpacing: '0.5px',
              lineHeight: '1.1'
            }}>
              ELUM LAW ASSOCIATES
            </span>
            <span style={{ 
              display: 'block', 
              fontSize: '0.72rem', 
              fontWeight: '700', 
              color: '#cbd5e1', 
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              marginTop: '2px'
            }}>
              AI Powered Legal Node • Pakistan
            </span>
          </div>
        </Link>

        {/* DESKTOP NAVIGATION BADGES BAR */}
        <nav style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          backgroundColor: '#0f172a',
          padding: '0.35rem 0.65rem',
          borderRadius: '24px',
          border: '1px solid rgba(236,201,75,0.3)',
          flexWrap: 'wrap',
          boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.4)'
        }}>
          
          {/* 1. Home */}
          <Link
            to="/"
            style={navItemStyle(location.pathname === '/', '#1e293b', '#e2e8f0')}
          >
            Home
          </Link>

          {/* 2. Law Dictionary */}
          <Link
            to="/law-directory"
            style={navItemStyle(location.pathname === '/law-directory', '#15803d', 'white')}
          >
            <BookOpen size={14} color="#ecc94b" />
            <span>Law Dictionary</span>
          </Link>

          {/* 3. Elum Law Directory */}
          <Link
            to="/lawyers"
            style={navItemStyle(location.pathname === '/lawyers', '#059669', 'white')}
          >
            <Scale size={14} color="#ecc94b" />
            <span>Law Directory</span>
          </Link>

          {/* 4. Elum Live Chat */}
          <Link
            to="/dashboard?tab=chat"
            style={navItemStyle(location.pathname === '/dashboard' && location.search.includes('chat'), '#16a34a', 'white')}
          >
            <MessageSquare size={14} color="white" />
            <span>Live Chat</span>
          </Link>

          {/* 5. Smart Legal Drafting & Pleading */}
          <Link
            to="/smart-legal-drafting"
            style={navItemStyle(location.pathname === '/smart-legal-drafting', '#d97706', '#0f172a')}
          >
            <Gavel size={14} color="#0f172a" />
            <span>Smart Drafting</span>
          </Link>

          {/* 6. AI Legal Assistant */}
          <Link
            to="/ai-assistant"
            style={navItemStyle(location.pathname === '/ai-assistant', '#4f46e5', 'white')}
          >
            <Shield size={14} color="#ecc94b" />
            <span>AI Assistant</span>
          </Link>

          {/* 7. Elum Meet (SINGLE DEFINITION - NEVER DOUBLED) */}
          <Link
            to="/conference"
            style={{
              ...navItemStyle(location.pathname === '/conference', '#0284c7', 'white'),
              border: '1.5px solid #38bdf8',
              boxShadow: '0 0 10px rgba(2,132,199,0.7)'
            }}
          >
            <Video size={14} color="#ecc94b" />
            <span>📹 Elum Meet</span>
          </Link>

          {/* 8. Downloads & Digital Publications */}
          <Link
            to="/downloads"
            style={{
              ...navItemStyle(location.pathname === '/downloads' || location.pathname === '/publications', '#7e22ce', 'white'),
              border: '1.5px solid #c084fc',
              boxShadow: '0 0 12px rgba(126,34,206,0.7)'
            }}
          >
            <Download size={14} color="#ecc94b" />
            <span>Downloads</span>
            <span style={{
              backgroundColor: '#ecc94b',
              color: '#0f172a',
              fontSize: '0.65rem',
              fontWeight: '900',
              padding: '0.1rem 0.4rem',
              borderRadius: '8px',
              marginLeft: '0.15rem'
            }}>
              NEW
            </span>
          </Link>

        </nav>

        {/* RIGHT UTILITIES: LANGUAGE, LOGIN, GET STARTED */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexShrink: 0 }}>
          {/* Language Switcher */}
          <button
            onClick={toggleLanguage}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.45rem 0.75rem',
              backgroundColor: '#1e293b',
              border: '1px solid #475569',
              borderRadius: '8px',
              color: '#ecc94b',
              fontSize: '0.78rem',
              fontWeight: '700',
              cursor: 'pointer'
            }}
            title="Switch Language (Urdu / English)"
          >
            <Globe size={14} />
            <span>{i18n.language === 'ur' ? 'English' : '🌐 اردو'}</span>
          </button>

          {/* Login */}
          <Link
            to="/login"
            style={{
              padding: '0.45rem 0.85rem',
              borderRadius: '8px',
              fontSize: '0.8rem',
              fontWeight: '700',
              color: '#e2e8f0',
              backgroundColor: '#1e293b',
              border: '1px solid #334155',
              textDecoration: 'none'
            }}
          >
            Login
          </Link>

          {/* Get Started */}
          <Link
            to="/dashboard"
            style={{
              padding: '0.45rem 1rem',
              borderRadius: '8px',
              fontSize: '0.8rem',
              fontWeight: '900',
              color: '#0f172a',
              backgroundColor: '#ecc94b',
              border: 'none',
              textDecoration: 'none',
              boxShadow: '0 0 10px rgba(236,201,75,0.4)'
            }}
          >
            Get Started
          </Link>

          {/* Mobile Menu Toggle Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              display: 'none',
              padding: '0.45rem',
              backgroundColor: '#1e293b',
              border: '1px solid #475569',
              borderRadius: '8px',
              color: '#e2e8f0',
              cursor: 'pointer'
            }}
            className="mobile-nav-toggle"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

      </div>

      {/* MOBILE DRAWER */}
      {mobileMenuOpen && (
        <div style={{
          backgroundColor: '#090d16',
          borderTop: '1px solid rgba(236,201,75,0.3)',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.6rem'
        }}>
          <Link to="/" onClick={() => setMobileMenuOpen(false)} style={navItemStyle(false, '#1e293b')}>Home Portal</Link>
          <Link to="/smart-legal-drafting" onClick={() => setMobileMenuOpen(false)} style={navItemStyle(false, '#d97706', '#0f172a')}>Smart Legal Drafting & Pleading</Link>
          <Link to="/ai-assistant" onClick={() => setMobileMenuOpen(false)} style={navItemStyle(false, '#4f46e5')}>AI Legal Assistant</Link>
          <Link to="/lawyers" onClick={() => setMobileMenuOpen(false)} style={navItemStyle(false, '#059669')}>Elum Law Directory</Link>
          <Link to="/law-directory" onClick={() => setMobileMenuOpen(false)} style={navItemStyle(false, '#15803d')}>Law Dictionary</Link>
          <Link to="/dashboard?tab=chat" onClick={() => setMobileMenuOpen(false)} style={navItemStyle(false, '#16a34a')}>Elum Live Chat</Link>
          <Link to="/conference" onClick={() => setMobileMenuOpen(false)} style={navItemStyle(false, '#0284c7')}>📹 Elum Meet (Video Conference)</Link>
          <Link to="/downloads" onClick={() => setMobileMenuOpen(false)} style={navItemStyle(true, '#7e22ce')}>📥 Digital Publications & Downloads</Link>
        </div>
      )}
    </header>
  );
};

export default Header;
